// ===================================================
// src/components/Planning/UploadProductionTargetsModal.js
// 📤 Upload Excel file with daily Total Production Target
// ===================================================
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { X, Save } from "lucide-react";

const UploadProductionTargetsModal = ({ onClose }) => {
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [percentages, setPercentages] = useState(null);

  // 🧮 Load production + M/L splits from DB
  useEffect(() => {
    fetch("/api/plan/percentages")
      .then((res) => res.json())
      .then((data) => setPercentages(data))
      .catch((err) => console.error("❌ Failed to load percentages:", err));
  }, []);

  // 📥 Handle Excel upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const parsed = [];
      for (let i = 1; i < data.length; i++) {
        const row = data[i];
        if (!row[0] || !row[1]) continue;

        let dateValue = row[0];
        let date;
        if (typeof dateValue === "number") {
          const excelEpoch = new Date(Date.UTC(1899, 11, 30));
          date = new Date(excelEpoch.getTime() + dateValue * 86400000);
        } else {
          date = new Date(dateValue);
        }

        const formattedDate = date.toISOString().split("T")[0];
        const totalProduction = parseFloat(row[1]) || 0;
        parsed.push({ date: formattedDate, totalProduction });
      }

      setRows(parsed);
    };
    reader.readAsBinaryString(file);
  };

  // 🚀 Upload to server
  const handleUpload = async () => {
    if (!rows.length) {
      alert("Please upload a valid Excel file first.");
      return;
    }
    if (!percentages) {
      alert("Percentages not loaded yet, please wait.");
      return;
    }

    if (!window.confirm(`Upload ${rows.length} days of Production Targets?`)) return;
    setUploading(true);

    try {
      // 🧩 1️⃣ Fetch existing plan
      const resExisting = await fetch("/api/plan/all");
      const existingData = await resExisting.json();
      const existingDays = existingData.days || [];

      const prodPerc = percentages.production || {};
      const mlProd = percentages.ml_production || {};

      // 🧮 Normalize production percentages so they always sum to 100%
      const totalPerc = Object.values(prodPerc).reduce((a, b) => a + b, 0) || 100;

      // 🧩 2️⃣ Build forecast.days
      const days = rows.map((row) => {
        const total = Number(row.totalProduction) || 0;
        const date = row.date;

        const existingDay = existingDays.find((d) => d.date === date) || {};
        const production = {};

        Object.keys(prodPerc).forEach((key) => {
          const normalizedPerc = (prodPerc[key] / totalPerc) * 100;
          const groupValue = (total * (normalizedPerc || 0)) / 100;
          const split = mlProd[key] || { m: 50, l: 50 };

          const mVal = Math.round(Number(groupValue * (split.m / 100)) || 0);
          const lVal = Math.round(Number(groupValue * (split.l / 100)) || 0);

          production[`${key}_m`] = parseInt(mVal, 10);
          production[`${key}_l`] = parseInt(lVal, 10);
        });

        // ✅ Clean sales values (avoid float precision)
        const cleanSales = {};
        if (existingDay.forecast?.sales) {
          Object.entries(existingDay.forecast.sales).forEach(([k, v]) => {
            cleanSales[k] = Math.round(Number(v) || 0);
          });
        }

        // 🧠 Merge back all forecast info
        return {
          date,
          forecast: {
            ...existingDay.forecast,
            sales: cleanSales,
            production,
          },
          sales: existingDay.sales || {},
          manufacturing: existingDay.manufacturing || {},
          stock: existingDay.stock || null,
          shipment: existingDay.shipment || {},
        };
      });

      // ✅ Final cleanup — ensure all numbers are integers
      days.forEach((d) => {
        if (d.forecast?.production) {
          Object.keys(d.forecast.production).forEach((k) => {
            d.forecast.production[k] = parseInt(d.forecast.production[k], 10) || 0;
          });
        }
      });

      // 🧩 3️⃣ Send to backend
      const res = await fetch("/api/plan/forecast", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days }),
      });

      if (!res.ok) throw new Error("Failed to upload production plan");

      alert("✅ Production plan merged successfully!");
      onClose();
    } catch (err) {
      console.error("❌ Upload failed:", err);
      alert("❌ Upload failed. Check console for details.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <h3 style={{ fontWeight: 600, fontSize: "1.25rem" }}>
            📤 Upload Daily Production Targets
          </h3>
          <button onClick={onClose} style={closeBtn}>
            <X size={20} />
          </button>
        </div>

        {/* File input */}
        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            style={{ marginBottom: "1rem" }}
          />
          <div style={{ fontSize: "0.9rem", color: "#6b7280" }}>
            Expected columns: <strong>Date</strong> | <strong>Total Production Target</strong>
          </div>
        </div>

        {/* Preview */}
        {rows.length > 0 && (
          <div style={{ maxHeight: "250px", overflowY: "auto", marginBottom: "1rem" }}>
            <table style={table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Total Production Target</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.date}</td>
                    <td>{r.totalProduction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Upload button */}
        <div style={{ textAlign: "right" }}>
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{
              background: "#10b981",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            <Save size={16} style={{ marginRight: "0.4rem" }} />
            {uploading ? "Uploading…" : "Upload to Server"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== Styles =====
const overlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
};
const modal = {
  background: "white",
  padding: "1.5rem 2rem",
  borderRadius: "10px",
  width: "600px",
  boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
  overflowY: "auto",
  maxHeight: "90vh",
};
const header = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "1rem",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "0.5rem",
};
const closeBtn = { background: "none", border: "none", cursor: "pointer" };
const table = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.9rem",
};
table.th = table.td = {
  border: "1px solid #e5e7eb",
  padding: "0.4rem 0.6rem",
  textAlign: "left",
};

export default UploadProductionTargetsModal;
